import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ONB } from "../constants/testIds4";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { CheckCircle, Circle, Clock, Plus, Trash, UploadSimple, FileText, UserCirclePlus, UserCircleMinus, Sparkle, FilePdf, VideoCamera, ShieldCheck, User, XCircle, Eye } from "@phosphor-icons/react";
import { Textarea } from "../components/ui/textarea";
import DocReviewPanel from "../components/DocReviewPanel";

const DOC_TYPES = ["aadhaar", "pan", "passport", "degree", "id_proof", "address_proof", "bank_proof", "resume", "previous_relieving", "other"];

export default function Onboarding() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);
  const [trackerKey, setTrackerKey] = useState(0);
  const isEmployee = !!user?.employee_id;

  const defaultTab = isEmployee && !isHR ? "portal" : "onb";

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">People operations</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Onboarding &amp; Documents</h1>
        <p className="text-sm text-zinc-500 mt-2">Standard checklists for joiners &amp; leavers plus personal document vault.</p>
      </div>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          {isEmployee && <TabsTrigger value="portal" className="rounded-sm" data-testid="onb-tab-portal"><Sparkle size={14} className="mr-1" /> My onboarding portal</TabsTrigger>}
          <TabsTrigger value="onb" className="rounded-sm" data-testid="onb-tab-onb"><UserCirclePlus size={14} className="mr-1" /> Onboarding</TabsTrigger>
          <TabsTrigger value="off" className="rounded-sm" data-testid="onb-tab-off"><UserCircleMinus size={14} className="mr-1" /> Offboarding</TabsTrigger>
          <TabsTrigger value="docs" className="rounded-sm" data-testid="onb-tab-docs"><FileText size={14} className="mr-1" /> Employee documents</TabsTrigger>
          {isHR && <TabsTrigger value="tracker" className="rounded-sm" data-testid="onb-tab-tracker"><ShieldCheck size={14} className="mr-1" /> Tracker</TabsTrigger>}
        </TabsList>

        {isEmployee && <TabsContent value="portal" className="mt-4"><PortalPanel /></TabsContent>}
        <TabsContent value="onb" className="mt-4"><WorkflowsPanel kind="onboarding" params={params} isHR={isHR} /></TabsContent>
        <TabsContent value="off" className="mt-4"><WorkflowsPanel kind="offboarding" params={params} isHR={isHR} /></TabsContent>
        <TabsContent value="docs" className="mt-4"><DocsPanel params={params} isHR={isHR} user={user} /></TabsContent>
        {isHR && <TabsContent value="tracker" className="mt-4 space-y-4"><DocReviewPanel params={params} onChanged={() => setTrackerKey((k) => k + 1)} /><TrackerPanel params={params} refreshKey={trackerKey} /></TabsContent>}
      </Tabs>
    </div>
  );
}

function PortalPanel() {
  const [prog, setProg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});

  const load = () => api.get("/onboarding/me").then(({ data }) => setProg(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const markDone = async (step) => {
    setBusy(true);
    try { await api.post("/onboarding/step-done", { step }); toast.success(`${step} confirmed`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const uploadDoc = async (docType, file) => {
    setUploading((u) => ({ ...u, [docType]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads?purpose=employee_doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/employee-docs", { doc_type: docType, storage_path: data.path, label: docType.replace(/_/g, " ") });
      toast.success(`${docType.replace(/_/g, " ")} uploaded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading((u) => ({ ...u, [docType]: false }));
  };

  const signPolicy = async (policy_id) => {
    try { await api.post("/onboarding/policy-signoff", { policy_id }); toast.success("Signed. Thank you"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!prog) return <div className="text-center py-16 text-sm text-zinc-500">Loading your onboarding portal…</div>;

  const stepsDoneCount = Object.values(prog.steps || {}).filter((s) => s.done).length;
  const totalSteps = 4;
  const percent = Math.round((stepsDoneCount / totalSteps) * 100);
  const kit = prog.kit || {};
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Progress + welcome */}
      <div className="lg:col-span-2 space-y-4">
        <div className="border border-zinc-200 bg-zinc-950 rounded-sm p-6 text-white orbit-grain-dark relative overflow-hidden" data-testid="portal-welcome-banner">
          <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-adb-green/10 blur-2xl pointer-events-none"></div>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-adb-green font-bold">Welcome aboard</div>
            <div className="font-display text-3xl font-bold mt-1">You're {percent}% through onboarding</div>
            <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-adb-green transition-all" style={{ width: `${percent}%` }} data-testid="portal-progress"></div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
              {Object.entries(prog.steps).map(([k, v]) => (
                <span key={k} className={`px-2 py-0.5 rounded-sm border ${v.done ? "border-adb-green/40 bg-adb-green/10 text-white" : "border-white/20 text-white/70"}`}>
                  {v.done ? "✓ " : "○ "}{k}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Step 1: Welcome */}
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="portal-step-welcome">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-sm font-bold ${prog.steps.welcome.done ? "bg-adb-green" : "bg-adb-navy"}`}>1</div>
              <div className="font-display font-bold text-lg">Watch our welcome</div>
            </div>
            {prog.steps.welcome.done && <Badge variant="outline" className="rounded-sm bg-emerald-50 border-emerald-200 text-emerald-800">Complete</Badge>}
          </div>
          {kit.welcome_message && <p className="mt-3 text-sm text-zinc-700 whitespace-pre-wrap">{kit.welcome_message}</p>}
          <div className="mt-3 flex flex-wrap gap-3">
            {kit.welcome_video_path && (
              <a href={`${backend}/api/files/${kit.welcome_video_path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-sm hover:bg-adb-navy/5 text-sm" data-testid="portal-welcome-video">
                <VideoCamera size={16} className="text-adb-navy" /> <span>Watch welcome video</span>
              </a>
            )}
            {kit.welcome_pdf_path && (
              <a href={`${backend}/api/files/${kit.welcome_pdf_path}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 border border-zinc-200 rounded-sm hover:bg-adb-navy/5 text-sm" data-testid="portal-welcome-pdf">
                <FilePdf size={16} className="text-adb-navy" /> <span>Company handbook</span>
              </a>
            )}
            {!kit.welcome_video_path && !kit.welcome_pdf_path && <div className="text-xs text-zinc-500">No welcome material uploaded yet — you can proceed.</div>}
          </div>
          {!prog.steps.welcome.done && <div className="mt-4"><Button onClick={() => markDone("welcome")} disabled={busy} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="portal-welcome-done">Mark as watched</Button></div>}
        </div>

        {/* Step 2: Documents */}
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="portal-step-docs">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-sm font-bold ${prog.steps.docs.done ? "bg-adb-green" : "bg-adb-navy"}`}>2</div>
              <div>
                <div className="font-display font-bold text-lg">Upload required documents <span className="text-red-600" title="All documents are mandatory">*</span></div>
                <div className="text-[10px] text-red-600">All attachments are mandatory. You can't advance until every document is uploaded.</div>
              </div>
            </div>
            <Badge variant="outline" className={`rounded-sm ${prog.missing_docs?.length === 0 ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              {(kit.required_doc_types || []).length - (prog.missing_docs?.length || 0)}/{(kit.required_doc_types || []).length} done
            </Badge>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {(kit.required_doc_types || []).map((d) => {
              const uploaded = prog.docs_uploaded?.find((x) => x.doc_type === d);
              const st = uploaded?.review_status || (uploaded ? "pending" : null);
              const rejected = st === "rejected";
              const tone = rejected || !uploaded ? "border-red-200 bg-red-50/30" : st === "approved" ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40";
              return (
                <div key={d} className={`border rounded-sm px-3 py-2 ${tone}`} data-testid={`portal-doc-row-${d}`}>
                  <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {st === "approved" ? <CheckCircle size={16} weight="fill" className="text-adb-green" /> : st === "pending" ? <Clock size={16} weight="fill" className="text-amber-500" /> : <Circle size={16} className="text-red-400" />}
                    <span className="text-sm capitalize">{d.replace(/_/g, " ")} <span className="text-red-600 text-xs">*</span></span>
                  </div>
                  {uploaded && !rejected ? (
                    <span className={`text-[10px] font-mono-plex ${st === "approved" ? "text-emerald-700" : "text-amber-700"}`} data-testid={`portal-doc-status-${d}`}>{st === "approved" ? "Approved by HR" : "Uploaded · awaiting HR review"}</span>
                  ) : (
                    <>
                      <input ref={(el) => (fileRefs.current[d] = el)} type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadDoc(d, e.target.files[0])} data-testid={`portal-doc-input-${d}`} />
                      <Button size="sm" variant="outline" onClick={() => fileRefs.current[d]?.click()} disabled={uploading[d]} className="rounded-sm text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid={`portal-doc-upload-${d}`}>
                        <UploadSimple size={12} className="mr-1" /> {uploading[d] ? "…" : rejected ? "Re-upload" : "Upload (required)"}
                      </Button>
                    </>
                  )}
                  </div>
                  {rejected && <div className="mt-1.5 text-[11px] text-red-700 bg-white/70 border border-red-200 rounded-sm px-2 py-1" data-testid={`portal-doc-comment-${d}`}><b>Rejected by HR:</b> {uploaded.review_comment}</div>}
                </div>
              );
            })}
          </div>
          <div className="mt-4">
            <Button
              onClick={() => markDone("docs")}
              disabled={busy || (prog.missing_docs?.length || 0) > 0 || prog.steps.docs.done}
              className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm disabled:opacity-50"
              data-testid="portal-docs-done"
            >
              {prog.steps.docs.done ? "Documents confirmed" : (prog.missing_docs?.length || 0) > 0 ? `Upload ${prog.missing_docs.length} more required document${prog.missing_docs.length === 1 ? "" : "s"}` : "Confirm all docs uploaded"}
            </Button>
          </div>
        </div>

        {/* Step 3: Policies */}
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="portal-step-policies">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-sm font-bold ${prog.steps.policies.done ? "bg-adb-green" : "bg-adb-navy"}`}>3</div>
              <div className="font-display font-bold text-lg">Read &amp; acknowledge policies</div>
            </div>
            <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200">
              {(prog.policies || []).length - (prog.unsigned_policies || []).length}/{(prog.policies || []).length} signed
            </Badge>
          </div>
          {(prog.policies || []).length === 0 && <div className="mt-3 text-xs text-zinc-500">No policies configured yet.</div>}
          <div className="mt-3 space-y-2">
            {(prog.policies || []).map((p) => {
              const signed = !((prog.unsigned_policies || []).find((u) => u.id === p.id));
              const unsignedFull = (prog.unsigned_policies || []).find((u) => u.id === p.id);
              return (
                <details key={p.id} className="border border-zinc-200 rounded-sm">
                  <summary className="px-3 py-2 flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      {signed ? <CheckCircle size={16} weight="fill" className="text-adb-green" /> : <Circle size={16} className="text-zinc-300" />}
                      <span className="text-sm font-medium">{p.title}</span>
                      <span className="text-[10px] font-mono-plex text-zinc-400">v{p.version}</span>
                    </div>
                    {signed && <Badge variant="outline" className="rounded-sm bg-emerald-50 border-emerald-200 text-emerald-800 text-[10px]">Signed</Badge>}
                  </summary>
                  {!signed && unsignedFull && (
                    <div className="border-t border-zinc-100 p-3 bg-zinc-50">
                      <article className="prose prose-sm max-w-none max-h-64 overflow-y-auto bg-white border border-zinc-200 rounded-sm p-3" dangerouslySetInnerHTML={{ __html: unsignedFull.body || "<p>No content.</p>" }} />
                      <div className="mt-2 flex justify-end">
                        <Button onClick={() => signPolicy(p.id)} className="bg-adb-green text-adb-navy hover:bg-adb-green-600 font-semibold rounded-sm" size="sm" data-testid={`portal-policy-sign-${p.kind}`}>I acknowledge &amp; agree</Button>
                      </div>
                    </div>
                  )}
                </details>
              );
            })}
          </div>
          {(prog.unsigned_policies || []).length === 0 && (prog.policies || []).length > 0 && !prog.steps.policies.done && (
            <div className="mt-4"><Button onClick={() => markDone("policies")} disabled={busy} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="portal-policies-done">Confirm all policies signed</Button></div>
          )}
        </div>

        {/* Step 4: Profile */}
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="portal-step-profile">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full grid place-items-center text-white text-sm font-bold ${prog.steps.profile.done ? "bg-adb-green" : "bg-adb-navy"}`}>4</div>
              <div className="font-display font-bold text-lg">Complete your profile</div>
            </div>
            {prog.steps.profile.done && <Badge variant="outline" className="rounded-sm bg-emerald-50 border-emerald-200 text-emerald-800">Complete</Badge>}
          </div>
          <div className="mt-3 text-sm text-zinc-600">Head to <a href="/app/profile" className="text-adb-navy underline font-medium">My Profile</a> and fill in your bank, address and emergency contact. When you're done, click the button below.</div>
          {!prog.steps.profile.done && <div className="mt-4"><Button onClick={() => markDone("profile")} disabled={busy} className="bg-adb-navy hover:bg-adb-navy-800 rounded-sm" data-testid="portal-profile-done">Mark profile complete</Button></div>}
        </div>

        {prog.completed_at && (
          <div className="border border-adb-green bg-adb-green/5 rounded-sm p-4 text-center">
            <CheckCircle size={28} weight="fill" className="mx-auto text-adb-green" />
            <div className="font-display font-bold mt-2">You're all set!</div>
            <div className="text-xs text-zinc-600">Completed on {new Date(prog.completed_at).toLocaleString()}</div>
          </div>
        )}
      </div>

      {/* Sidebar summary */}
      <div className="space-y-4">
        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Your checklist</div>
          <div className="mt-2 space-y-1.5 text-sm">
            {Object.entries(prog.steps).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                {v.done ? <CheckCircle size={16} weight="fill" className="text-adb-green" /> : <Circle size={16} className="text-zinc-300" />}
                <span className={v.done ? "text-zinc-400 line-through capitalize" : "capitalize"}>{k}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Need help?</div>
          <div className="text-xs text-zinc-600 mt-2">Reach out to HR or ask Zenith360 AI (bottom-right chat) if any step is unclear.</div>
        </div>
      </div>
    </div>
  );
}

function TrackerPanel({ params, refreshKey }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/onboarding/tracker", { params }).then(({ data }) => setRows(data)).catch(() => setRows([])); /* eslint-disable-next-line */ }, [params.tenant_id, refreshKey]);
  return (
    <div className="border border-zinc-200 rounded-sm overflow-x-auto bg-white">
      <table className="hr-table">
        <thead><tr><th>Employee</th><th>Progress</th><th>Docs (approved / uploaded)</th><th>Policies signed</th><th>Completed</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-zinc-500 text-sm">No onboarding progress records yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.employee_id} data-testid={`tracker-row-${r.employee_id}`}>
              <td>
                <div className="flex items-center gap-2"><User size={14} className="text-adb-navy" /><span className="text-sm">{r.employee_name}</span></div>
              </td>
              <td>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-1.5 bg-zinc-200 rounded-full overflow-hidden"><div className="h-full bg-adb-green" style={{ width: `${r.progress_percent}%` }}></div></div>
                  <span className="font-mono-plex text-xs">{r.progress_percent}%</span>
                </div>
              </td>
              <td className="font-mono-plex text-xs">{r.approved_docs_count ?? 0} / {r.uploaded_docs_count}{r.pending_docs_count ? <span className="ml-1 text-amber-600">({r.pending_docs_count} pending)</span> : null}</td>
              <td className="font-mono-plex text-xs">{r.signed_policies_count}</td>
              <td className="font-mono-plex text-xs">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkflowsPanel({ kind, params, isHR }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [emp, setEmp] = useState("");

  const load = () => api.get("/workflows", { params: { ...params, kind } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [kind, params.tenant_id]);

  const start = async () => {
    try { await api.post("/workflows/start", { employee_id: emp, kind }); toast.success("Workflow started"); setOpen(false); setEmp(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async (wid, task_key, completed) => {
    try { await api.patch(`/workflows/${wid}/tasks`, { task_key, completed }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      {isHR && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={kind === "onboarding" ? ONB.startOnBtn : ONB.startOffBtn}><Plus size={16} className="mr-1.5" /> Start {kind}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl capitalize">Start {kind}</DialogTitle></DialogHeader>
              <div className="space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={emp} onValueChange={setEmp}>
                  <SelectTrigger className="rounded-sm" data-testid="wf-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={start} disabled={!emp} className="btn-zenith rounded-sm" data-testid="wf-start-btn">Start</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="space-y-3" data-testid={ONB.wfList}>
        {rows.length === 0 && <div className="border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-500 rounded-sm">No {kind} workflows.</div>}
        {rows.map((w) => (
          <div key={w.id} className="border border-zinc-200 bg-white rounded-sm">
            <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="font-display text-lg font-bold">{w.employee_name}</div>
                <div className="text-xs font-mono-plex text-zinc-500">{w.employee_code}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-300 font-mono-plex">{w.progress?.done}/{w.progress?.total}</Badge>
                <Badge variant="outline" className={`rounded-sm ${w.status === "completed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}>{w.status}</Badge>
              </div>
            </div>
            <div className="divide-y divide-zinc-100">
              {(w.tasks || []).map((t) => (
                <div key={t.key} className="flex items-center gap-3 px-4 py-2">
                  <button onClick={() => toggle(w.id, t.key, t.status !== "done")} data-testid={ONB.taskToggle(w.id, t.key)} className="shrink-0">
                    {t.status === "done" ? <CheckCircle size={20} weight="fill" className="text-emerald-700" /> : <Circle size={20} className="text-zinc-300" />}
                  </button>
                  <div className="flex-1">
                    <div className={`text-sm ${t.status === "done" ? "text-zinc-400 line-through" : "text-zinc-800"}`}>{t.title}</div>
                    {t.completed_at && <div className="text-[10px] text-zinc-400 font-mono-plex">{new Date(t.completed_at).toLocaleString()}</div>}
                  </div>
                  <Badge variant="outline" className="rounded-sm bg-zinc-50 border-zinc-200 text-[10px] uppercase">{t.owner_role}</Badge>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocsPanel({ params, isHR, user }) {
  const [employees, setEmployees] = useState([]);
  const [empId, setEmpId] = useState("");
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doc_type: "aadhaar", label: "", number: "", expiry: "", storage_path: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data));
    if (user?.employee_id && !empId) setEmpId(user.employee_id);
    // eslint-disable-next-line
  }, [params.tenant_id, isHR]);

  useEffect(() => {
    if (empId) api.get("/employee-docs", { params: { employee_id: empId } }).then(({ data }) => setDocs(data)).catch(() => setDocs([]));
  }, [empId]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads?purpose=employee_doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, storage_path: data.path });
      toast.success("File uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  const register = async () => {
    try {
      await api.post("/employee-docs", { ...form, employee_id: empId });
      toast.success("Document saved");
      setOpen(false);
      setForm({ doc_type: "aadhaar", label: "", number: "", expiry: "", storage_path: "" });
      api.get("/employee-docs", { params: { employee_id: empId } }).then(({ data }) => setDocs(data));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/employee-docs/${id}`); setDocs(docs.filter((d) => d.id !== id)); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="w-full md:w-[300px]">
          <Label className="text-xs">Employee</Label>
          <Select value={empId} onValueChange={setEmpId}>
            <SelectTrigger className="rounded-sm" data-testid="docs-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {user?.employee_id && <SelectItem value={user.employee_id}>Myself</SelectItem>}
              {isHR && employees.filter((e) => e.id !== user?.employee_id).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {empId && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={ONB.docsAddBtn}><Plus size={16} className="mr-1.5" /> Add document</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Register document</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Type</Label>
                  <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d} value={d}>{d.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="rounded-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Number / ID</Label><Input value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                <div className="space-y-1"><Label className="text-xs">Expiry</Label><Input type="date" value={form.expiry} onChange={(e) => setForm({ ...form, expiry: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs">File</Label>
                  <input ref={fileRef} type="file" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="hidden" data-testid={ONB.docsUploadBtn} />
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="doc-file-btn"><UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Choose file"}</Button>
                    {form.storage_path && <span className="text-xs text-emerald-800 font-mono-plex truncate">{form.storage_path.split("/").pop()}</span>}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={register} disabled={!form.storage_path} className="btn-zenith rounded-sm" data-testid="doc-save-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {empId && (
        <div className="border border-zinc-200 rounded-sm overflow-x-auto">
          <table className="hr-table">
            <thead><tr><th>Type</th><th>Label</th><th>Number</th><th>Expiry</th><th>Uploaded</th><th></th></tr></thead>
            <tbody>
              {docs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-zinc-500 text-sm">No documents.</td></tr>}
              {docs.map((d) => (
                <tr key={d.id}>
                  <td className="capitalize">{d.doc_type.replace(/_/g, " ")}</td>
                  <td>{d.label || "—"}</td>
                  <td className="font-mono-plex text-xs">{d.number || "—"}</td>
                  <td className="font-mono-plex text-xs">{d.expiry || "—"}</td>
                  <td className="font-mono-plex text-xs">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td><button onClick={() => del(d.id)} className="text-red-600 hover:opacity-70" data-testid={`doc-del-${d.id}`}><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
